import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Portal,
  Snackbar,
} from '@mui/material';
import { BaseTheme } from 'design-system';
import { ActionView as Action, ButtonView as Button } from 'interfaces/graphqlViews';
import { useEffect, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { Link } from 'react-router';
import { getPrefixedRoute } from 'services/theming';
import { hasIOSNativeDownloadHandler } from 'utils/download-file';
import isCustomScheme from 'utils/is-custom-scheme';
import { isIOS } from 'utils/is-ios';
import { downloadPdf, methodDownloadPdfPage, pdfDownloadErrorMessage } from 'utils/market-pdf';

const DownloadActionItem = ({
  action,
  nested = false,
  onClick,
}: {
  action: Action;
  nested?: boolean;
  onClick: (action?: Action) => void;
}) => {
  const [isDownloading, setDownloading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const messageHandler = (event: MessageEvent) => {
      // Only handle postMessages from this window with object payloads.
      if (event.source !== window) return;
      if (!event.data || typeof event.data !== 'object') return;

      const { action: downloadAction } = event.data as {
        action?: 'downloadComplete' | 'downloadError';
      };

      if (!downloadAction) return;

      setDownloading(false);

      switch (downloadAction) {
        case 'downloadComplete':
          onClick();
          break;
        case 'downloadError':
          setErrorMessage(pdfDownloadErrorMessage(null));
          break;
      }
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, [onClick]);

  const handleClick = async () => {
    setDownloading(true);
    setErrorMessage(null);

    try {
      await downloadPdf(methodDownloadPdfPage(action.url));
      // In the Nile iOS app the native bridge reports the outcome to the listener above.
      if (!hasIOSNativeDownloadHandler()) {
        setDownloading(false);
        onClick();
      }
    } catch (error) {
      setDownloading(false);
      setErrorMessage(pdfDownloadErrorMessage(error));
    }
  };

  return (
    <>
      <ListItemButton onClick={handleClick} disabled={isDownloading}>
        <ListItemText
          disableTypography={nested}
          sx={(theme) => (nested ? { paddingLeft: theme.spacing(0.75) } : {})}
          primary={action.value}
          secondary={action.description}
        />
        {isDownloading && <CircularProgress size={20} color="primary" />}
      </ListItemButton>
      {isDownloading && (
        <Portal>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
          >
            <CircularProgress size={40} sx={{ color: 'white' }} />
          </Box>
        </Portal>
      )}
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={errorMessage !== null}
        autoHideDuration={3000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

const ActionItem = ({
  action,
  onClick,
  nested = false,
}: {
  action: Action;
  onClick: (action?: Action) => void;
  nested?: boolean;
}) => {
  const isIOSDevice = isIOS();

  const style = (theme: BaseTheme) =>
    nested
      ? {
          paddingLeft: theme.spacing(0.75),
        }
      : {};

  const isLink = action.url.length > 0 && !isCustomScheme(action.url);

  if (isLink) {
    const isExternal = action.url.startsWith('http');
    if (isExternal) {
      return (
        <ListItemButton component="a" href={action.url} target="_blank" onClick={() => onClick()}>
          <ListItemText disableTypography={nested} sx={style} primary={action.value} secondary={action.description} />
        </ListItemButton>
      );
    }
    const to = getPrefixedRoute(action.url);
    return (
      <ListItemButton
        component={Link}
        to={to.startsWith('#') ? to.slice(1) : to.replace('/#/', '')}
        onClick={() => onClick()}
      >
        <ListItemText disableTypography={nested} sx={style} primary={action.value} secondary={action.description} />
      </ListItemButton>
    );
  }

  // iOS: the native app's WKWebView download bridge (same as DownloadPdfButton), or
  // in Safari a fetch of the native endpoint, which a plain link cannot authenticate.
  if (isIOSDevice && action.url.includes('method:download')) {
    return <DownloadActionItem nested={nested} onClick={onClick} action={action} />;
  }

  return (
    <ListItemButton
      onClick={() => {
        onClick(action);
      }}
    >
      <ListItemText disableTypography={nested} sx={style} primary={action.value} secondary={action.description} />

      {/* TODO: Fix loader logic */}
      {/* {isLoading && <CircularProgress size={20} color="primary" />} */}
    </ListItemButton>
  );
};

const ActionGroup = ({
  action,
  onClick,
  nested = false,
}: {
  action: Action;
  nested?: boolean;
  onClick: (action?: Action) => void;
}) => {
  const [open, setOpen] = useState(false);
  const handleClick = () => setOpen(!open);

  if (!action.group || action.group.length === 0) {
    return <ActionItem nested={nested} onClick={onClick} action={action} />;
  }

  return (
    <Box sx={(theme) => ({ paddingLeft: nested ? theme.spacing(0.75) : 0 })}>
      <ListItemButton onClick={handleClick}>
        <ListItemText primary={action.value} />
        {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
      </ListItemButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {action.group.map((action, i) => {
            if (!action) return null;
            return <ActionGroup nested key={`${action.value + i}`} onClick={onClick} action={action} />;
          })}
        </List>
      </Collapse>
    </Box>
  );
};

const ActionList = ({
  open,
  button,
  onClose,
}: {
  open: boolean;
  button: Button;
  onClose: (action?: Action) => void;
}) => {
  const handleClose = () => {
    onClose();
  };

  const handleSelect = (action?: Action) => {
    onClose(action);
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{button.value}</DialogTitle>
      <DialogContent>
        <List>
          {button.actions.map((action, i) => {
            return <ActionGroup key={`${action.value + i}`} onClick={handleSelect} action={action} />;
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};

export default ActionList;
